import axios from "axios";

const BASE_URL =
  process.env.REACT_APP_API_URL || "https://beta-brnx-api.borderless.world";

// ─── Authenticated Axios instance ────────────────────────────────────────────
// All web API calls go through this instance.
// Base path for every endpoint: /web/v1/...
// The request interceptor attaches the user's JWT as a Bearer token on every
// request. Pre-auth calls (login) are made directly with plain axios so they
// are never affected by this interceptor.
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

// ── Request interceptor ───────────────────────────────────────────────────────
// Attach Authorization: Bearer <accessToken> when the user is logged in.
api.interceptors.request.use((config) => {
  try {
    const token = sessionStorage.getItem("brnx_access_token");
    if (token) config.headers["authorization"] = token;
  } catch {
    // sessionStorage unavailable (e.g. in some private-browse contexts)
  }
  return config;
});

// ── Response interceptor — silent token refresh ───────────────────────────────
// On a 401, attempt one refresh and replay the original request once.
// All requests that arrive while a refresh is in-flight are queued.
let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  refreshQueue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config;

    if (err.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((e) => Promise.reject(e));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = sessionStorage.getItem("brnx_refresh_token");
        if (!refreshToken) throw new Error("No refresh token stored");

        const res = await axios.post(
          `${BASE_URL}/web/v1/auth/refresh-token`,
          { refreshToken },
          { headers: { "Content-Type": "application/json" } }
        );

        const { accessToken, refreshToken: newRefresh } = res.data.data;
        sessionStorage.setItem("brnx_access_token", accessToken);
        sessionStorage.setItem("brnx_refresh_token", newRefresh);

        processQueue(null, accessToken);
        originalRequest.headers["authorization"] = accessToken;
        return api(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        sessionStorage.clear();
        window.location.href = "/auth/login";
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  }
);

export default api;
